import { hasProperty } from '@chiroup/core/functions/hasProperty';
import { regionDisplay } from '@chiroup/core/functions/regionDisplay';
import { escapeHtmlTags } from '@chiroup/core/functions/stripHtmlTags';
import { camelCaseToTitleCase, titleCase } from '@chiroup/core/functions/titleCase';
import { Muscle } from '@chiroup/core/types/Muscle.type';
import {
  CarePlanComplaint,
  ImprovementOptionsText,
  Visit,
} from '@chiroup/core/types/PatientVisit.type';
import { getSpineDisplayName, getSpineSortWeight, Regions } from '../../../../../constants';
import { CustomizationListItem } from '../../../../clinical-skills/CustomizationListItem.type';

const jointsNameHash: { [key in string]: string } = {
  E: 'Elbow',
  'F/A': 'Foot/Ankle',
  'H/W': 'Hand/Wrist',
  H: 'Hip',
  K: 'Knee',
  RIBS: 'Ribs',
  S: 'Shoulder',
  SPINE: 'Spine',
  HEAD: 'Head',
};

const nerveAssessmentNames = {
  cervical: 'Spinal Nerve Assessment | Cervical',
  lumbar: 'Spinal Nerve Assessment | Lumbar',
  upper: 'Peripheral Nerve Assessment | Upper Extremity',
  lower: 'Peripheral Nerve Assessment | Lower Extremity',
  central: 'Central Nervous System Assessment',
  cranial: 'Cranial Nerve Assessment',
} as const;

type NerveRegions = keyof typeof nerveAssessmentNames;

// Helper function to handle 'Other' and 'Abnormal' values with explanations
const getExplanationOrChoice = (
  value: { choice?: string | string[]; explanation?: string } | undefined
) => {
  if (!value) return undefined;
  if (!value.choice) return value.choice;

  if (typeof value.choice === 'string') {
    const isSpecialChoice = value.choice === 'Other' || value.choice === 'Abnormal';

    if (isSpecialChoice) {
      const hasExplanationText = hasProperty(value, 'explanation') && !!value.explanation;

      return hasExplanationText ? value.explanation : value.choice;
    }

    return value.choice;
  }

  if (Array.isArray(value.choice)) {
    const containsAbnormal = value.choice.includes('Abnormal');

    if (containsAbnormal) {
      const filteredChoices = value.choice.filter(choiceItem => choiceItem !== 'Abnormal');
      const explanationOrAbnormal =
        hasProperty(value, 'explanation') && value.explanation ? value.explanation : 'Abnormal';

      return [...filteredChoices, explanationOrAbnormal].join(', ');
    }

    return value.choice.join(', ');
  }

  return value.choice;
};

const isSideObject = (value: any): value is { left?: boolean; right?: boolean } => {
  return typeof value === 'object' && (hasProperty(value, 'left') || hasProperty(value, 'right'));
};

function spinalSort(
  a: [string, string | { left?: boolean | undefined; right?: boolean | undefined }],
  b: [string, string | { left?: boolean | undefined; right?: boolean | undefined }]
) {
  const orderA = getSpineSortWeight(a[0]) ?? Number.MAX_SAFE_INTEGER;
  const orderB = getSpineSortWeight(b[0]) ?? Number.MAX_SAFE_INTEGER;

  return orderA - orderB;
}

const romKeyNameValueHash: {
  [key in string]: { [key in string]: { name: string; maxValue: string } };
} = {
  C: {
    extension: { name: 'Extension', maxValue: '60' },
    flexion: { name: 'Flexion', maxValue: '50' },
    leftRotation: { name: 'Left Rotation', maxValue: '80' },
    rightRotation: { name: 'Right Rotation', maxValue: '80' },
    leftLateralFlexion: { name: 'Left Lateral Flexion', maxValue: '45' },
    rightLateralFlexion: { name: 'Right Lateral Flexion', maxValue: '45' },
  },
  E: {
    extension: { name: 'Extension', maxValue: '0' },
    flexion: { name: 'Flexion', maxValue: '150' },
    pronation: { name: 'Pronation', maxValue: '90' },
    supination: { name: 'Supination', maxValue: '90' },
  },
  S: {
    extension: { name: 'Extension', maxValue: '50' },
    adduction: { name: 'Adduction', maxValue: '0' },
    elevatedAbduction: { name: 'Elevated Abduction', maxValue: '150' },
    elevatedForwardFlexion: {
      name: 'Elevated Forward Flexion',
      maxValue: '180',
    },
    externalRotation: { name: 'External Rotation', maxValue: '90' },
    internalRotation: { name: 'Internal Rotation', maxValue: '80' },
  },
  'H/W': {
    forearmPronation: { name: 'Forearm Pronation', maxValue: '90' },
    forearmSupination: { name: 'Forearm Supination', maxValue: '90' },
    wristExtension: { name: 'Wrist Extension', maxValue: '80' },
    wristFlexion: { name: 'Wrist Flexion', maxValue: '90' },
    wristRadialDeviation: { name: 'Wrist Radial Deviation', maxValue: '20' },
    wristUlnarDeviation: { name: 'Wrist Ulnar Deviation', maxValue: '45' },
  },
  T: {
    extension: { name: 'Extension', maxValue: '30' },
    flexion: { name: 'Flexion', maxValue: '30' },
    leftLateralFlexion: { name: 'Left Lateral Flexion', maxValue: '30' },
    rightLateralFlexion: { name: 'Right Lateral Flexion', maxValue: '30' },
    leftRotation: { name: 'Left Rotation', maxValue: '40' },
    rightRotation: { name: 'Right Rotation', maxValue: '40' },
  },
  L: {
    extension: { name: 'Extension', maxValue: '30' },
    flexion: { name: 'Flexion', maxValue: '60' },
    leftLateralFlexion: { name: 'Left Lateral Flexion', maxValue: '25' },
    rightLateralFlexion: { name: 'Right Lateral Flexion', maxValue: '25' },
    leftRotation: { name: 'Left Rotation', maxValue: '30' },
    rightRotation: { name: 'Right Rotation', maxValue: '30' },
  },
  H: {
    extension: { name: 'Extension', maxValue: '15' },
    flexion: { name: 'Flexion', maxValue: '125' },
    abduction: { name: 'Abduction', maxValue: '45' },
    adduction: { name: 'Adduction', maxValue: '20' },
    internalRotation: { name: 'Internal Rotation', maxValue: '45' },
    externalRotation: { name: 'External Rotation', maxValue: '45' },
  },
  K: {
    flexion: { name: 'Flexion', maxValue: '140' },
    extension: { name: 'Extension', maxValue: '0' },
  },
  'F/A': {
    ankleDorsiflexion: { name: 'Ankle Dorsi Flexion', maxValue: '20' },
    anklePlantarflexion: { name: 'Ankle Plantar Flexion', maxValue: '45' },
    footEversion: { name: 'Foot Eversion', maxValue: '25' },
    footInversion: { name: 'Foot Inversion', maxValue: '35' },
  },
};

export const getOrthopedicContent = (
  orthopedicObj: {
    detail?: {
      [key in string]: {
        value?: string;
        side?: string;
        testNotes?: string;
        touched?: boolean;
      };
    };
    value?: string;
  },
  evals: CustomizationListItem[],
  lastVisitObjectiveData: {
    detail?: {
      [key in string]: { value?: string; side?: string; testNotes?: string };
    };
  }
) => {
  const posAndNegArr = Object.entries(orthopedicObj?.detail || {}).reduce(
    (arr, val) => {
      const evaluation = evals.find(
        item => item.ID === Number(val[0]) || item.parentID === Number(val[0])
      );
      if (!val[1]?.touched) return arr;

      if (val[1].value === 'positive' && evaluation?.name) {
        arr[0] = {
          ...arr[0],
          [evaluation?.name]: {
            name: evaluation?.name,
            testNotes: val[1]?.testNotes,
            side: val[1]?.side,
            touched: val[1]?.touched,
            soapOutput: evaluation?.soapOutput,
          },
        };

        if (lastVisitObjectiveData?.detail?.[val[0]]?.value === 'negative' && evaluation?.name) {
          arr[2] = {
            ...arr[2],
            [evaluation?.name]: {
              name: evaluation?.name,
              testNotes: val[1]?.testNotes,
              side: val[1]?.side,
              prevVal: 'negative',
            },
          };
        }
      }

      if (val[1].value === 'negative' && evaluation?.name) {
        arr[1] = {
          ...arr[1],
          [evaluation?.name]: {
            name: evaluation?.name,
            testNotes: val[1]?.testNotes,
            side: val[1]?.side,
            soapOutput: evaluation?.soapOutput,
          },
        };

        if (lastVisitObjectiveData?.detail?.[val[0]]?.value === 'positive' && evaluation?.name) {
          arr[2] = {
            ...arr[2],
            [evaluation?.name]: {
              name: evaluation?.name,
              testNotes: val[1]?.testNotes,
              side: val[1]?.side,
              prevVal: 'positive',
            },
          };
        }
      }

      return arr;
    },
    [{}, {}, {}]
  );
  let completedStr = '';

  completedStr += posAndNegArr.reduce(
    (
      str,
      val: {
        [key in string]: {
          name?: string;
          testNotes?: string;
          side?: string;
          prevVal?: string;
          soapOutput?: string;
        };
      },
      index
    ) => {
      if (index === 0 && Object.values(val)?.length) {
        str += `<ul><li>Positive orthopedic findings</li>`;
        str += '<ul>';
        str += Object.values(val).reduce(
          (
            innerStr,
            item: { name?: string; side?: string; testNotes?: string; soapOutput?: string }
          ) => {
            if (item?.soapOutput) {
              innerStr += `<li>${item.name}: ${item.soapOutput}</li>`;
            } else {
              innerStr += `<li>${item.name} ${item?.side ? '(' + item?.side + ')' : ''} ${
                item?.testNotes ? '(' + item.testNotes + ')' : ''
              }</li>`;
            }

            return innerStr;
          },
          ''
        );
        str += '</ul></ul>';
      } else if (index === 1 && Object.values(val)?.length) {
        str += `<ul><li>Negative orthopedic findings</li>`;
        str += '<ul>';
        str += Object.values(val).reduce(
          (
            innerStr,
            item: { name?: string; side?: string; testNotes?: string; soapOutput?: string }
          ) => {
            if (item?.soapOutput) {
              innerStr += `<li>${item.name}: ${item.soapOutput}</li>`;
            } else {
              innerStr += `<li>${item.name} ${item?.side ? '(' + item?.side + ')' : ''} ${
                item?.testNotes ? '(' + item.testNotes + ')' : ''
              }</li>`;
            }

            return innerStr;
          },
          ''
        );
        str += '</ul></ul>';
      } else if (index === 2) {
        const arr = Object.values(val);

        if (arr.length) {
          str += `<ul><li>Significant changes</li>`;
          str += '<ul>';
          str += arr.reduce(
            (
              innerStr,
              item: {
                name?: string;
                side?: string;
                testNotes?: string;
                prevVal?: string;
              }
            ) => {
              innerStr += `<li>${item?.name} is no longer ${item?.prevVal || ''}</li>`;

              return innerStr;
            },
            ''
          );
        }

        str += '</ul></ul>';
      }

      return str;
    },
    ''
  );

  return completedStr;
};

export const getMyofascialContent = (
  myofascialObj: {
    detail?: {
      [key in string]: {
        [key in string]?: boolean;
      };
    };
    value?: string;
  },
  muscles: Muscle[],
  lastVisitObjectiveData: {
    detail?: {
      [key in string]: {
        [key in string]?: boolean;
      };
    };
    value?: string;
  },
  anyTouched: boolean
) => {
  if (!anyTouched) {
    const namesArr = Object.entries(myofascialObj?.detail || {}).reduce((arr: string[], val) => {
      // Skip meta keys like __regions and any non-numeric keys
      const id = Number(val[0]);
      if (Number.isNaN(id)) return arr;

      const muscle = muscles.find(muscle => muscle.ID === id);

      if (muscle && muscle.name) {
        arr.push(muscle.name);
      }

      return arr;
    }, []);

    return namesArr.length ? `Affected muscles include the ${namesArr.join(', ')}.</p>` : '';
  }

  return Object.entries(myofascialObj?.detail || {}).reduce((str, val) => {
    // Skip meta keys like __regions and any non-numeric keys
    const id = Number(val[0]);
    if (Number.isNaN(id)) return str;

    const muscle = muscles.find(muscle => muscle.ID === id);
    if (!muscle) return str;
    const valArrs = Object.entries(val[1]).reduce((arr, v) => {
      if (v[0] === 'tender' && v[1]) {
        arr.push(' tenderness');
      } else if (v[0] === 'hpn' && v[1]) {
        arr.push(' hypertonicity');
      } else if (v[0] === 'tp' && v[1]) {
        arr.push(' trigger point');
      } else if (v[0] === 'spasm' && v[1]) {
        arr.push(' spasms');
      } else if (v[0] === 'weakness' && v[1]) {
        arr.push(' weakness');
      }

      return arr;
    }, [] as string[]);

    const newlyAddedMuscle = !lastVisitObjectiveData?.detail?.[val[0]];
    const noLongerSymptoms = Object.entries(lastVisitObjectiveData?.detail?.[val[0]] || {}).reduce(
      (arr, v) => {
        if (v[0] === 'tender' && v[1] && !myofascialObj?.detail?.[val[0]]?.tender) {
          arr.push('tenderness');
        } else if (v[0] === 'hpn' && v[1] && !myofascialObj?.detail?.[val[0]]?.hpn) {
          arr.push('hypertonicity');
        } else if (v[0] === 'tp' && v[1] && !myofascialObj?.detail?.[val[0]]?.tp) {
          arr.push('trigger point');
        } else if (v[0] === 'spasm' && v[1] && !myofascialObj?.detail?.[val[0]]?.spasm) {
          arr.push('spasms');
        } else if (v[0] === 'weakness' && v[1] && !myofascialObj?.detail?.[val[0]]?.weakness) {
          arr.push('weakness');
        }

        return arr;
      },
      [] as string[]
    );

    const symptomsList = newlyAddedMuscle
      ? `${valArrs?.length ? valArrs.join(',') + '.' : ''}`
      : `
      ${valArrs?.length ? 'Persistence of ' + valArrs.join(', ') + '. ' : ''}${
        noLongerSymptoms?.length ? 'No presence of ' + `${noLongerSymptoms.join(', ')}.` : ''
      }`.trim();

    if (symptomsList?.length) {
      str += '<ul>';
      str += `<li>`;
      if (val[1].left) str += `Left `;
      if (val[1].right) str += `Right `;
      str += `${muscle?.name}: <span style="font-weight: bold;">${symptomsList}</span>`;
      str += '</li></ul>';
    }

    return str;
  }, '');
};

export const getROMContent = (
  romObj: {
    detail?: {
      [key in Regions]: {
        [key in string]: {
          [key in string]: {
            value?: number;
            pain?: boolean;
          };
        };
      };
    };
    value?: string;
  },
  lastVisitObjectiveData: {
    detail?: {
      [key in Regions]: {
        [key in string]: {
          [key in string]: { value?: number; pain?: boolean };
        };
      };
    };
    value?: string;
  }
) => {
  let romString = '';
  const hasRomValue = (val?: number) => !!val || val === 0;
  // "Clear all" leaves every movement in place with empty values and marks the
  // region touched, so touched alone does not mean there is anything to report.
  const movementHasFinding = (
    regionKey: string,
    movementData?: { [key in string]: { value?: number; pain?: boolean } }
  ) =>
    regionKey === 'C' || regionKey === 'T' || regionKey === 'L'
      ? hasRomValue(movementData?.SpinalArea?.value) || !!movementData?.SpinalArea?.pain
      : hasRomValue(movementData?.Left?.value) || hasRomValue(movementData?.Right?.value);

  Object.keys(romObj?.detail || {}).forEach(regionKey => {
    const regionData = romObj?.detail?.[regionKey as Regions];
    const region = regionDisplay(regionKey);
    const regionTouched =
      romObj?.detail?.[regionKey as Regions]?.touched &&
      Object.keys(regionData || {}).some(
        key =>
          key !== 'touched' &&
          key !== 'complaintIds' &&
          key !== 'addedManually' &&
          movementHasFinding(regionKey, regionData?.[key])
      );

    // check if region has actual ROM data (not just metadata)
    const hasActualROMData = Object.keys(regionData || {}).some(
      key => key !== 'touched' && key !== 'complaintIds' && key !== 'addedManually'
    );

    // skip regions with no actual ROM data or that weren't touched
    if (!hasActualROMData && !regionTouched) return;

    const header = romObj?.value
      ? `<span style="font-weight: bold;">${region} range of motion</span> is ${
          ImprovementOptionsText[romObj.value as keyof typeof ImprovementOptionsText]
        } compared to the preceding visit. ${
          regionTouched ? 'Limitations persist in the following.' : '</br>'
        } `
      : regionTouched
        ? `<span style="font-weight: bold;">${region} ROM:</span>`
        : '';
    romString += header;

    if (!regionTouched) return;
    Object.keys(regionData || {}).forEach(romKey => {
      // Skip metadata fields from V2 unified objective
      if (romKey === 'touched' || romKey === 'complaintIds' || romKey === 'addedManually') {
        return;
      }
      if (!movementHasFinding(regionKey, regionData?.[romKey])) return;

      const spinalData = regionKey === 'C' || regionKey === 'T' || regionKey === 'L';
      const movement = romKeyNameValueHash?.[regionKey]?.[romKey]?.name;
      const romMaxValue = romKeyNameValueHash?.[regionKey]?.[romKey]?.maxValue;

      if (spinalData) {
        const spinalRomValue = regionData?.[romKey]?.SpinalArea?.value;
        const spinalPainfulValue = regionData?.[romKey]?.SpinalArea?.pain ? 'painful' : '';

        const lastVisitSpinalValue =
          lastVisitObjectiveData?.detail?.[regionKey as Regions]?.[romKey]?.SpinalArea?.value;
        const lastVisitSpinalPain =
          lastVisitObjectiveData?.detail?.[regionKey as Regions]?.[romKey]?.SpinalArea?.pain;

        const noLongerPainful =
          !regionData?.[romKey]?.SpinalArea?.pain && lastVisitSpinalPain ? 'no longer painful' : '';
        const didRangeChange = !lastVisitSpinalValue
          ? ''
          : spinalRomValue === lastVisitSpinalValue
            ? ', unchanged'
            : Math.abs(Number(romMaxValue) - Number(spinalRomValue)) <
                Math.abs(Number(romMaxValue) - Number(lastVisitSpinalValue))
              ? ', improved'
              : ', more limited';

        romString += '<ul>';
        romString += `<li>${movement}: ${
          spinalRomValue ? `${spinalRomValue}/(${romMaxValue})` : ''
        }${
          spinalRomValue && spinalPainfulValue?.length
            ? `, ${spinalPainfulValue}`
            : `${titleCase(spinalPainfulValue)}`
        }${
          spinalRomValue && noLongerPainful
            ? `, ${noLongerPainful}`
            : `${titleCase(noLongerPainful)}`
        }${didRangeChange}</li>`;
        romString += '</ul>';
      } else {
        //leftSide
        const leftSideRomValue = regionData?.[romKey]?.Left?.value;
        const leftSidePainfulValue = regionData?.[romKey]?.Left?.pain ? 'painful' : '';
        const lastVisitLeftSideValue =
          lastVisitObjectiveData?.detail?.[regionKey as Regions]?.[romKey]?.Left?.value;
        const lastVisitLeftSidePainful =
          lastVisitObjectiveData?.detail?.[regionKey as Regions]?.[romKey]?.Left?.pain;
        const leftSidePainful = regionData?.[romKey]?.Left?.pain ? 'painful' : '';
        const didLeftSideRangeChange = !lastVisitLeftSideValue
          ? ''
          : leftSideRomValue === lastVisitLeftSideValue
            ? ', unchanged'
            : Math.abs(Number(romMaxValue) - Number(leftSideRomValue)) <
                Math.abs(Number(romMaxValue) - Number(lastVisitLeftSideValue))
              ? ', improved'
              : ', more limited';
        const leftSideNoLongerPainful =
          !regionData?.[romKey]?.Left?.pain && lastVisitLeftSidePainful ? 'no longer painful' : '';
        //rightSide

        const rightSideRomValue = regionData?.[romKey]?.Right?.value;
        const rightSidePainful = regionData?.[romKey]?.Right?.pain ? 'painful' : '';
        const lastVisitRightSideValue =
          lastVisitObjectiveData?.detail?.[regionKey as Regions]?.[romKey]?.Right?.value;
        const lastVisitRightSidePainful =
          lastVisitObjectiveData?.detail?.[regionKey as Regions]?.[romKey]?.Right?.pain;
        const didRightSideRangeChange = !lastVisitRightSideValue
          ? ''
          : rightSideRomValue === lastVisitRightSideValue
            ? ', unchanged'
            : Math.abs(Number(romMaxValue) - Number(rightSideRomValue)) <
                Math.abs(Number(romMaxValue) - Number(lastVisitRightSideValue))
              ? ', improved'
              : ', more limited';
        const RightSideNoLongerPainful =
          !regionData?.[romKey]?.Right?.pain && lastVisitRightSidePainful
            ? 'no longer painful'
            : '';

        romString += '<ul>';
        romString += `<li>${movement}: </li><ul>`;

        if (leftSideRomValue || leftSideRomValue === 0) {
          romString += `<li>Left: ${`${leftSideRomValue}/(${romMaxValue})`}${
            leftSideRomValue && leftSidePainfulValue
              ? `, ${leftSidePainful}`
              : `${titleCase(leftSidePainful)}`
          }${
            leftSideRomValue && leftSideNoLongerPainful.length
              ? `, ${leftSideNoLongerPainful}`
              : `${titleCase(leftSideNoLongerPainful)}`
          }${didLeftSideRangeChange}</li>`;
        }

        if (rightSideRomValue || rightSideRomValue === 0) {
          romString += `<li>Right: ${`${rightSideRomValue}/(${romMaxValue})`}${
            rightSideRomValue && rightSidePainful.length
              ? `, ${rightSidePainful}`
              : `${titleCase(rightSidePainful)}`
          }${
            rightSideRomValue && RightSideNoLongerPainful
              ? `, ${RightSideNoLongerPainful}`
              : `${titleCase(RightSideNoLongerPainful)}`
          }${didRightSideRangeChange}</li>`;
        }

        romString += '</ul></ul>';
      }
    });
  });

  return romString;
};

export const getNeurologicalContent = (neurologicalObj: {
  [key in NerveRegions]: {
    result?: string;
    detail?: {
      [key in string]: {
        [key in string]: {
          choice?: string | string[];
          explanation?: string;
          left?: { choice: string; explanation?: string };
          right?: { choice: string; explanation?: string };
        };
      };
    };
  };
}) => {
  return (Object.keys(neurologicalObj) as NerveRegions[]).reduce(
    (str, nerveRegion: NerveRegions) => {
      const name = nerveAssessmentNames[nerveRegion];
      const detail = neurologicalObj?.[nerveRegion]?.detail;

      str += `<span style="font-weight: bold;">${name}</span>`;

      if (detail) {
        const sortedDetailEntries = ['lumbar', 'cervical'].includes(nerveRegion)
          ? Object.entries(detail).sort(spinalSort)
          : Object.entries(detail);

        if (['lumbar', 'cervical'].includes(nerveRegion)) {
          // Group by Sensory / Motor / Reflex; indent data underneath
          const tests = ['sensory', 'motor', 'reflex'] as const;
          const byTestStr = tests.reduce((acc, testName) => {
            const perLevel = sortedDetailEntries
              .map(([nerve, nerveData]) => {
                const neuroTestResult = (nerveData as any)?.[testName];
                if (!neuroTestResult) return '';

                const parts: string[] = [];

                if (neuroTestResult?.left) {
                  parts.push(
                    `Left: <span style="font-weight: bold;"> ${getExplanationOrChoice(neuroTestResult.left)}</span>`
                  );
                }

                if (neuroTestResult?.right) {
                  parts.push(
                    `Right: <span style="font-weight: bold;"> ${getExplanationOrChoice(neuroTestResult.right)}</span>`
                  );
                }

                if (!parts.length && neuroTestResult?.choice) {
                  parts.push(
                    `<span style="font-weight: bold;">${getExplanationOrChoice(neuroTestResult)}</span>`
                  );
                }

                if (!parts.length) return '';

                return `${nerve}: ${parts.join(', ')}`;
              })
              .filter(Boolean)
              .join('; ');

            if (!perLevel) return acc;
            acc += `<ul style="margin:4px 0 8px 0; padding-left:16px; list-style:disc;">`;
            acc += `<li style="margin:0 0 4px 0; padding:0;">${camelCaseToTitleCase(testName)}`;
            acc += `<ul style="margin:2px 0 0 16px; padding-left:16px; list-style:disc;">`;
            acc += `<li style="margin:0; padding:0;">${perLevel}</li>`;
            acc += `</ul>`;
            acc += `</li>`;
            acc += `</ul>`;

            return acc;
          }, '');

          str += byTestStr;
        } else {
          str += sortedDetailEntries
            ?.sort((a, b) => {
              const order: Record<string, number> = {
                CNI: 1,
                CNIIIIIIVVI: 2,
                CNV: 3,
                CNVII: 4,
                CNVIII: 5,
                CNIXX: 6,
                CNXI: 7,
                CNXII: 8,
              };

              return (order[a[0]] || 99) - (order[b[0]] || 99);
            })
            ?.reduce((str1, [nerve, nerveData]) => {
              const cranialKeyLabel: Record<string, string> = {
                CNI: 'CN I',
                CNIIIIIIVVI: 'CN II, III, IV, VI',
                CNV: 'CN V',
                CNVII: 'CN VII',
                CNVIII: 'CN VIII',
                CNIXX: 'CN IX, X',
                CNXI: 'CN XI',
                CNXII: 'CN XII',
              };
              const centralLabelMap: Record<string, string> = {
                mentalStatus: 'Mental Status',
                mentalstatus: 'Mental Status',
                coordination: 'Coordination',
                pathologicReflexes: 'Pathologic Reflexes',
                pathologicreflexes: 'Pathologic Reflexes',
              };
              const nerveName =
                nerveRegion === 'cranial'
                  ? (cranialKeyLabel[nerve] ?? nerve)
                  : (centralLabelMap[nerve] ?? camelCaseToTitleCase(nerve));
              const isCranial = nerveRegion === 'cranial';

              str1 += '<ul style="margin:0;padding-left:0;list-style:none;">';
              str1 += `<li style="margin:0;">${nerveName}`;

              if (isCranial) {
                str1 +=
                  `: <span style="font-weight: bold;">` +
                  getExplanationOrChoice(nerveData) +
                  `</span>`;
              }

              str1 += '</li>';

              if (!isCranial) {
                const detailKey = detail?.[nerve];

                if (detailKey) {
                  str1 += Object.entries(detailKey)?.reduce(
                    (str2, [neuroTestName, neuroTestResult]) => {
                      str2 += '<ul style="margin:0;padding-left:0;list-style:none;">';
                      str2 += `<li style="margin:0;">${camelCaseToTitleCase(neuroTestName)}: `;

                      if (neuroTestResult?.left || neuroTestResult?.right) {
                        if (neuroTestResult?.left) {
                          str2 += `Left: <span style="font-weight: bold;"> ${getExplanationOrChoice(neuroTestResult.left)}</span>`;
                        }

                        if (neuroTestResult?.right) {
                          str2 += neuroTestResult?.left ? ', ' : '';
                          str2 += `Right: <span style="font-weight: bold;"> ${getExplanationOrChoice(neuroTestResult.right)}</span>`;
                        }
                      } else if (neuroTestResult?.choice) {
                        str2 +=
                          '<span style="font-weight: bold;">' +
                          getExplanationOrChoice(neuroTestResult) +
                          '</span>';
                      }

                      str2 += '</li>';
                      str2 += '</ul>';

                      return str2;
                    },
                    ''
                  );
                }
              }

              str1 += '</ul>';

              return str1;
            }, '');
        }
      }

      return str;
    },
    ''
  );
};

export const getJointContent = (jointObj: {
  detail?: {
    [key: string]: {
      [key: string]: string | { left?: boolean; right?: boolean };
    };
  };
  value?: string;
}) => {
  if (!jointObj?.detail) return '';
  const spineGlobalLabels = new Set<string>();

  let list = Object.keys(jointObj.detail).reduce((str, jointArea) => {
    const currentDetail = jointObj.detail?.[jointArea] ?? {};
    const entries = Object.entries(currentDetail);
    if (!entries.length) return str;

    if (jointArea === 'SPINE') {
      entries.sort(spinalSort);
      const onlyLLI = entries.every(part => part[0] === 'LLI');
      if (onlyLLI) return str;
    }

    const addedLabels = jointArea === 'SPINE' ? spineGlobalLabels : new Set<string>();

    const innerStr = entries.reduce((acc, [partKey, value], index) => {
      if (isSideObject(value)) {
        acc += `${
          value.right && value.left
            ? ' Bilateral '
            : value.left
              ? ' Left '
              : value.right
                ? ' Right '
                : ''
        } ${partKey}, `;
      } else if (jointArea === 'SPINE' && partKey !== 'LLI') {
        const name = getSpineDisplayName(partKey);

        if (partKey === 'PL' || partKey === 'PR') {
          if (value === 'BOTH') {
            const iliacLabel = partKey === 'PL' ? 'Left Ilium' : 'Right Ilium';

            if (!addedLabels.has(iliacLabel)) {
              addedLabels.add(iliacLabel);
              acc += `${iliacLabel}, `;
            }
          }
        } else if (partKey === 'S') {
          const sideText =
            value === 'BOTH'
              ? 'Bilateral '
              : value === 'LEFT'
                ? 'Left '
                : value === 'RIGHT'
                  ? 'Right '
                  : '';
          const label = `${sideText}${name}`;

          if (!addedLabels.has(label)) {
            addedLabels.add(label);
            acc += `${label}, `;
          }
        } else if (partKey === 'C') {
          const sideText =
            value === 'BOTH'
              ? 'Bilateral '
              : value === 'LEFT'
                ? 'Left '
                : value === 'RIGHT'
                  ? 'Right '
                  : '';
          const label = `${sideText}${name}`;

          if (!addedLabels.has(label)) {
            addedLabels.add(label);
            acc += `${label}, `;
          }
        } else {
          const label = `${value === 'LEFT' ? 'Left ' : value === 'RIGHT' ? 'Right ' : ''}${name}`;

          if (!addedLabels.has(label)) {
            addedLabels.add(label);
            acc += `${label}, `;
          }
        }
      }

      // Remove the trailing comma for the last item
      if (index === entries.length - 1) {
        acc = acc.slice(0, -2);
      }

      return acc;
    }, '');

    str += `<p><span style="font-weight: bold";>${jointsNameHash[jointArea]} ${
      jointObj?.value
        ? ` joint dysfunction</span> is <u>${
            ImprovementOptionsText[jointObj.value as keyof typeof ImprovementOptionsText]
          }</u> as compared with previous visit. Joint dysfunction, characterized by hypomobility and palpable asymmetry with associated tenderness to palpation,
          muscle hypertonicity, and soft tissue edema is present at: `
        : ` joint dysfunction</span>, characterized by hypomobility and palpable asymmetry with associated tenderness to palpation,
          muscle hypertonicity, and soft tissue edema is present at: `
    }${innerStr}.</p>`;

    return str;
  }, '');

  if (jointObj?.detail?.SPINE?.LLI) {
    const entries = Object.entries(jointObj.detail.SPINE.LLI);
    const side = entries.length > 0 ? entries[0][1] : undefined;
    const value = entries.length > 1 ? entries[1][1] : undefined;

    if (side && value) {
      list += `<li>${titleCase(
        side
      )}-sided <strong>leg length inequality</strong> of ${value}mm.</li>`;
    }
  }

  return list;
};

export const getWYSIWYGObjectiveContentFn = (
  visitForm: Visit,
  activeComplaintsProblemAreas: { [key in string]: boolean },
  muscles: { data: Muscle[] },
  evals: { data: CustomizationListItem[] },
  complaints: CarePlanComplaint[]
) => {
  const lastVisitObjectiveData = visitForm?.lastVisitObjectiveData || {};
  const complaintsLength = Object.keys(activeComplaintsProblemAreas)?.length;
  //i need complaint order and can get from looking at the subjective piece.
  const visitFormKeysInOrder = complaints?.map(complaint => complaint.id) || [];

  return visitFormKeysInOrder.reduce((str, complaintId, index) => {
    if (!complaintId) return str;
    const complaintName = complaints?.find(c => c.id === complaintId)?.complaint;

    const complaintNote = visitForm?.note?.[complaintId];

    const hasObjectiveData = Object.keys(complaintNote?.objective || {}).length;
    if (!hasObjectiveData) return str;

    const hasObjectiveOrthopedicDetail = Object.values(
      complaintNote?.objective?.Orthopedic?.detail || {}
    ).some((det: any) => !!det?.value);
    const hasOnlyOrthopedic = !!(hasObjectiveData === 1 && complaintNote?.objective?.Orthopedic);
    if (hasOnlyOrthopedic && !hasObjectiveOrthopedicDetail) return str;

    let additionalContent = '';

    if (complaintNote?.objective?.Joints) {
      additionalContent += getJointContent(complaintNote?.objective?.Joints || {});
    }

    if (complaintNote?.objective?.RangeOfMotion) {
      additionalContent += getROMContent(
        complaintNote?.objective?.RangeOfMotion || {},
        lastVisitObjectiveData?.[complaintId]?.RangeOfMotion || {}
      );
    }

    const detail: { [key: string]: { touched: boolean } } = complaintNote?.objective?.Myofascial
      ?.detail || {
      touched: false,
    };

    const value = complaintNote?.objective?.Myofascial?.value;

    const anyMyofascialTouched = Object.values(detail).some(obj => obj.touched);

    if (anyMyofascialTouched || value) {
      const header = value
        ? `<p><span style="font-weight: bold">Myofascial involvement is</span> ${ImprovementOptionsText[value]} as compared to the preceding visit. `
        : `<span style="font-weight: bold;">Myofascial Involvement:</span>`;

      additionalContent += header;
      additionalContent += getMyofascialContent(
        complaintNote?.objective?.Myofascial || {},
        muscles?.data || [],
        lastVisitObjectiveData?.[complaintId]?.Myofascial || {},
        anyMyofascialTouched
      );
    }

    if (complaintNote?.objective?.Orthopedic) {
      const showOrtho = Object.values(complaintNote?.objective?.Orthopedic?.detail).find(
        (item: any) => item.touched
      ) as {
        touched: boolean;
        ID: number;
        side: string;
        testNotes: string;
        value: string;
      };

      const header = complaintNote?.objective?.Orthopedic?.value
        ? `<p><span style="font-weight: bold;">Orthopedic evaluation findings</span> are ${
            ImprovementOptionsText[complaintNote.objective.Orthopedic.value]
          } as compared to the preceding visit.</p>`
        : showOrtho
          ? `<span style="font-weight: bold;">Orthopedic evaluation findings:</span>`
          : '';
      const bodyDetail = getOrthopedicContent(
        complaintNote?.objective?.Orthopedic || {},
        evals?.data || [],
        lastVisitObjectiveData?.[complaintId]?.Orthopedic || {}
      );

      additionalContent +=
        bodyDetail !== '</ul></ul>'
          ? header + bodyDetail
          : complaintNote?.objective?.Orthopedic?.value
            ? header
            : '';
    }

    if (complaintNote?.objective?.Neurological) {
      const header = complaintNote?.objective?.Neurological?.value
        ? `<p><span style="font-weight: bold;">Neurological evaluation findings</span> are ${
            ImprovementOptionsText[complaintNote.objective.Neurological.value]
          } as compared to the preceding visit.</p>`
        : ``;
      additionalContent += header;
      additionalContent += getNeurologicalContent(
        complaintNote?.objective?.Neurological?.detail || {}
      );
    }

    if (complaintNote?.objective?.extraNotes) {
      const escapedExtraNotes = escapeHtmlTags(complaintNote.objective.extraNotes);
      additionalContent += `<p><span style="font-weight: bold;">Notes: </span><span style-"font-weight: normal;">${escapedExtraNotes}</span></p>`;
    }

    if (additionalContent) {
      str += `<h3><span style="font-weight: semi-bold;" >${titleCase(
        complaintName || ''
      )}</span></h3>`;
      str += `<p>Complaint ${index + 1} of ${complaintsLength}</p>`;
      str += additionalContent;
    }

    return str;
  }, '');
};
